<template>
  <!-- No surface of its own: the conversation is a white column sitting directly
       on the page's grey canvas. -->
  <div class="ai-workspace" :class="{ 'is-docked': historyDocked }">
    <div class="ai-workspace__inner">
      <div class="ai-workspace__main">
        <div class="ai-workspace__surface">
          <!-- Fixed: it sits outside the scrolling region, so the title stays put
               however far back through the conversation you scroll. -->
          <header class="ai-chat-header">
            <span class="ai-chat-header__title">{{ activeTitle }}</span>
          </header>

        <!-- Landing: an empty chat you can begin typing into. Not a resumed
             transcript — "a chat window you can begin chatting to" only holds if
             you land on an empty one. -->
        <div v-if="isLanding" class="ai-workspace__landing">
          <div class="ai-workspace__landing-inner">
            <h1 class="ai-workspace__landing-title">AI Workspace</h1>
            <p class="ai-workspace__landing-sub">
              A thinking tool — ask questions, draft, stress-test a plan.
              Don't paste customer identifiers.
            </p>

            <!-- Content, not chrome: it disappears the moment you type, so the
                 fast path stays "just start typing". -->
            <button
              v-if="resumable && !composerText"
              type="button"
              class="ai-workspace__resume"
              @click="openConversation(resumable)"
            >
              Continue: {{ resumable.label }} — {{ relativeTime(resumable.lastActivityAt) }}
            </button>
          </div>
        </div>

        <MessageTranscript
          v-else
          :messages="messages"
          :skill-names="skillNames"
          @retry="retryMessage"
        />
        </div>

        <!-- Standing state, not an error: the workspace is off, or this role was never
             given a quota profile. Shown before anything is typed, because the
             alternative is composing a paragraph into an input that was never going to
             send it. The two say different things on purpose — one is an incident, the
             other is a five-minute admin fix. -->
        <div v-if="unavailableNotice" class="ai-workspace__refusal-wrap">
          <div class="ai-workspace__notice">
            <i class="fa fa-info-circle"></i>
            <div class="ai-workspace__refusal-body">
              <div class="font-weight-bold">{{ unavailableNotice.title }}</div>
              <div>{{ unavailableNotice.detail }}</div>
            </div>
          </div>
        </div>

        <!-- Refusals stay put rather than fading. ENG-2245 distinguishes "no quota
             profile" from "no permission" precisely so someone can read it,
             screenshot it and send it to an admin — a toast would throw that away. -->
        <div v-if="refusal" class="ai-workspace__refusal-wrap">
          <div class="ai-workspace__refusal" :class="{ 'is-block': refusal.isBlock }">
            <i :class="refusal.isBlock ? 'fa fa-ban' : 'fa fa-exclamation-circle'"></i>
            <div class="ai-workspace__refusal-body">
              <div>{{ refusal.message }}</div>
              <div v-if="refusal.code" class="ai-workspace__refusal-code">{{ refusal.code }}</div>
            </div>
            <button type="button" title="Dismiss" @click="refusal = null">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <MessageComposer
          v-model="composerText"
          :skills="skills"
          :active-skill-id="activeSkillId"
          :disabled="sending || hasUnresolvedMessage || Boolean(unavailableNotice)"
          :max-chars="maxChars"
          @update:activeSkillId="activeSkillId = $event"
          @send="send"
        />
      </div>
    </div>

    <!-- Open/close arrow on the right edge, vertically centred — the same idea as
         the ARCH nav's own toggle. It rides the panel edge so it is never covered. -->
    <button
      type="button"
      class="ai-workspace__rail-toggle"
      :class="{ 'is-open': historyOpen }"
      :title="historyOpen ? 'Hide conversation history' : 'Show conversation history'"
      @click="historyOpen = !historyOpen"
    >
      <i :class="historyOpen ? 'fa fa-chevron-circle-right' : 'fa fa-chevron-circle-left'"></i>
    </button>

    <!-- Pinned to the right edge, mirroring the ARCH nav on the left. Docked when
         there is room beside the centred conversation, otherwise it overlays. -->
    <transition name="ai-rail">
      <ConversationHistoryPanel
        v-if="historyOpen"
        :docked="historyDocked"
        :conversations="conversations"
        :active-id="activeConversationId"
        :loading="conversationsLoading"
        @close="closeHistory"
        @new="startNew"
        @open="openConversation"
        @rename="renameConversation"
        @hide="hideConversation"
        @settings="settingsOpen = true"
      />
    </transition>

    <!-- Settings over the workspace rather than a screen away from it: the numbers
         being changed are the ones governing the conversation underneath, and an
         administrator raising a limit usually wants to go straight back to the message
         that hit it. Rendered only while open, so each visit reads the current config
         rather than whatever was loaded the first time. -->
    <!-- No header bar and no close button: the panel carries its own title, state pill
         and switch, and clicking outside is the way out. The switch applies on the click
         and profile and role changes save as they are made, so the only thing a stray
         backdrop click costs is unsaved edits in the Settings form. -->
    <b-modal
      v-model="settingsOpen"
      size="xl"
      dialog-class="ai-settings-dialog"
      scrollable
      hide-header
      hide-footer
    >
      <AiSettingsPanel v-if="settingsOpen" />
    </b-modal>
  </div>
</template>


<script>
import moment from "moment";
import AiWorkspaceService from "@/services/aiWorkspaceService";
import { streamMessage, supportsStreaming } from "@/services/aiStreamService";
import { describeApiError } from "@/utils/ai/apiError";
import MessageTranscript from "./MessageTranscript.vue";
import MessageComposer from "./MessageComposer.vue";
import ConversationHistoryPanel from "./ConversationHistoryPanel.vue";
import AiSettingsPanel from "./AiSettingsPanel.vue";

/** How recently you must have been in a conversation for the landing to offer it back. */
const RESUME_WINDOW_MS = 30 * 60 * 1000;

/** Matches the transcript's patience — past this, a pending message is dead, not slow. */
const PATIENCE_MS = 330 * 1000;

const POLL_INTERVAL_MS = 3000;

/**
 * Width needed before the rail can sit open without covering the conversation.
 *
 * The conversation stays centred in the content area regardless of the rail, so
 * the rail only avoids it when there is 280px of clearance on *both* sides of the
 * 784px column — otherwise the centred column would run under it. Below this the
 * rail overlays instead.
 *
 * Measured against the real container rather than the viewport, because the ARCH
 * sidebar collapses and a viewport breakpoint would be wrong by its width.
 */
const DOCK_MIN_PX = 784 + 280 * 2;

export default {
  name: "AiWorkspaceComponent",

  components: {
    MessageTranscript,
    MessageComposer,
    ConversationHistoryPanel,
    AiSettingsPanel,
  },

  data() {
    return {
      skills: [],
      activeSkillId: null,
      conversations: [],
      conversationsLoading: false,
      activeConversationId: null,
      messages: [],
      composerText: "",
      sending: false,
      refusal: null,
      historyOpen: false,
      // The settings modal, opened from the gear at the foot of the history rail.
      // Only `ai.config.manage` holders are offered it; the panel's own calls are
      // refused server-side for anyone else.
      settingsOpen: false,
      // Whether there is room to dock the rail beside the conversation. When there
      // is, it is simply open; when there is not, it becomes a toggled overlay.
      historyDocked: false,
      // skillVersionId -> Skill name, for messages sent in this session. History
      // returns version ids only and the skills endpoint exposes none, so changes
      // loaded from history are detected but cannot be named until `skillName`
      // lands on MessageView.
      skillNames: {},
      maxChars: null,
      // Two separate facts from `GET ai/me` (ADR-0007): the workspace-wide kill
      // switches, and whether this user's own role has a quota profile. Both default to
      // the permissive answer — the send is the authoritative gate, and a transient
      // failure here must not black out a working workspace.
      workspaceEnabled: true,
      quota: null,
      pollTimer: null,
      hasActivated: false,
      localSeq: 0,
      // The assistant row currently being written into by `delta` events. Held by id
      // rather than by reference because the array is rebuilt around it.
      streamingMessageId: null,
    };
  },

  computed: {
    isLanding() {
      return !this.activeConversationId && !this.messages.length;
    },

    defaultSkill() {
      return this.skills.find((skill) => skill.isDefault) || this.skills[0] || null;
    },

    hasUnresolvedMessage() {
      return this.messages.some(
        (message) =>
          (message.status === "pending" || message.status === "streaming") &&
          Date.now() - new Date(message.createdAt).getTime() < PATIENCE_MS,
      );
    },

    /**
     * Title for the chat header.
     *
     * Uses the list's `label`, which is the stored title when one exists and the
     * derived `Skill — date` otherwise, so the header is never blank. Model-generated
     * titles arrive with ENG-2249; until then this reads as the derived label.
     */
    activeTitle() {
      if (!this.activeConversationId) {
        return "New conversation";
      }
      const active = this.conversations.find(
        (conversation) => conversation.id === this.activeConversationId,
      );
      return active ? active.label : "Conversation";
    },

    /**
     * Why the composer is inert, when it is.
     *
     * The workspace being off and this role having no quota profile are kept apart
     * because the remedies are nothing alike: one is an incident someone is already
     * dealing with, the other is an administrator assigning a profile. The workspace
     * reason deliberately says nothing about *which* switch is off — that is
     * operational detail the availability endpoint does not report and a chat user
     * cannot act on.
     */
    unavailableNotice() {
      if (!this.workspaceEnabled) {
        return {
          title: "The AI workspace is turned off",
          detail:
            "It has been switched off for everyone. Messages cannot be sent until it is back on — " +
            "your conversations are still here and still readable.",
        };
      }

      // Absent `quota` means the backend has not shipped enforcement, which is not the
      // same as a role having no profile and must not be read as a denial.
      if (this.quota && this.quota.configured === false) {
        return {
          title: "Your role has no AI usage limits configured",
          detail:
            "Ask an administrator to assign a quota profile to your role. Until they do, " +
            "every message is refused — this is not a permission problem.",
        };
      }

      return null;
    },

    /** The most recent conversation, if it was recent enough to be worth offering. */
    resumable() {
      const candidate = this.conversations[0];
      if (!candidate || !candidate.lastActivityAt) {
        return null;
      }
      const age = Date.now() - new Date(candidate.lastActivityAt).getTime();
      return age < RESUME_WINDOW_MS ? candidate : null;
    },
  },

  created() {
    this.loadStatus();
    this.loadSkills();
    this.loadConversations();
  },


  /**
   * This component is kept alive (see App.vue), so `mounted` runs once while
   * `activated` runs on every return. Anything that would accumulate across repeat
   * navigation belongs here rather than in `mounted`.
   *
   * Polling is deliberately NOT stopped on deactivate — an answer still being
   * generated should keep being tracked while the user is on another ARCH screen,
   * which is the entire reason this component is cached.
   */
  mounted() {
    this.updateDocking();
  },

  activated() {
    this.$nextTick(this.updateDocking);
    window.addEventListener("resize", this.updateDocking);

    if (this.hasActivated) {
      // A conversation may have been renamed or hidden in another tab, and an
      // answer that landed while we were away is worth picking up.
      this.loadConversations();
      this.syncPolling();
      // The kill switch is meant to take effect within seconds. This component is
      // cached, so without re-asking, a user who was already on the screen when it was
      // flipped would keep a live-looking composer for as long as they stayed logged in.
      this.loadStatus();
      // The picker is the other half of the settings screen. Publishing a Skill is a
      // decision taken on a different route and reaches everyone on their next message,
      // so the author who just took it must not be the one person who cannot see it —
      // which is what `created()` alone means for a component that is cached.
      this.loadSkills();
    }
    this.hasActivated = true;
  },

  deactivated() {
    window.removeEventListener("resize", this.updateDocking);
  },

  /**
   * Deliberately does not cancel an answer in flight.
   *
   * Closing the tab does not stop the generation: the backend keeps consuming the
   * provider stream and still writes the finished answer, so someone who shuts their
   * laptop mid-answer finds it waiting on a refreshed page. Aborting here would throw
   * away work that is already being paid for and cannot be resumed — there is no stream
   * reattachment, by design.
   */
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDocking);
    this.stopPolling();
  },

  methods: {
    relativeTime(value) {
      return value ? moment(value).fromNow() : "";
    },

    /**
     * Availability and this user's quota, in one call on load.
     *
     * `maxMessageChars` comes from here rather than being guessed: the limit is the
     * user's own profile's, checked against the raw text before any detector or
     * provider work, so the composer's counter is the same number the gate uses.
     */
    async loadStatus() {
      const status = await AiWorkspaceService.getWorkspaceStatus();

      this.workspaceEnabled = status.enabled !== false;
      this.quota = status.quota || null;
      this.maxChars =
        this.quota && this.quota.maxMessageChars ? this.quota.maxMessageChars : null;
    },

    async loadSkills() {
      try {
        const response = await AiWorkspaceService.getSkills();
        this.skills = response.data || [];

        // Reloading means the selection can outlive the list: a Skill archived while the
        // user was on another screen leaves an id here that is no longer offered, which
        // renders as a nameless picker and a send the backend refuses. Falling back to
        // the default is the same thing that happens to a new conversation.
        const stillOffered =
          this.activeSkillId && this.skills.some((skill) => skill.id === this.activeSkillId);
        if (!stillOffered) {
          this.activeSkillId = this.defaultSkill ? this.defaultSkill.id : null;
        }
      } catch (error) {
        this.setRefusal(error);
      }
    },

    async loadConversations() {
      this.conversationsLoading = true;
      try {
        const response = await AiWorkspaceService.getConversations();
        this.conversations = response.data || [];
      } catch {
        // The history panel falls back to its empty state; there is nothing else to say.
      } finally {
        this.conversationsLoading = false;
      }
    },

    startNew() {
      this.activeConversationId = null;
      this.streamingMessageId = null;
      this.messages = [];
      this.refusal = null;
      this.composerText = "";
      this.activeSkillId = this.defaultSkill ? this.defaultSkill.id : null;
      this.stopPolling();
      // You asked for a blank chat; leaving the panel open means the first thing you
      // see is the list you just left.
      this.historyOpen = false;
    },

    /**
     * Decide whether the rail docks, from the width actually available.
     *
     * Only acts when the answer changes, so a user who deliberately closed a docked
     * rail does not have it reopened by an incidental resize.
     */
    updateDocking() {
      const container = this.$el && this.$el.parentElement;
      const available = container ? container.clientWidth : window.innerWidth;
      const canDock = available >= DOCK_MIN_PX;

      if (canDock === this.historyDocked) {
        return;
      }

      this.historyDocked = canDock;
      // Room for it means it is simply part of the layout; no room means it goes
      // back to being something you open when you want it.
      this.historyOpen = canDock;
    },

    closeHistory() {
      this.historyOpen = false;
    },

    async openConversation(conversation) {
      this.activeConversationId = conversation.id;
      this.refusal = null;
      this.streamingMessageId = null;
      this.messages = [];
      this.stopPolling();

      // Only dismiss it when it is an overlay. Docked, it is part of the layout and
      // closing it on every selection would make the list jump away as you browse.
      if (!this.historyDocked) {
        this.historyOpen = false;
      }

      await this.loadHistory();
    },

    async loadHistory() {
      if (!this.activeConversationId) {
        return;
      }

      try {
        const response = await AiWorkspaceService.getHistory(this.activeConversationId);
        this.messages = response.data || [];
        this.carryForwardSkill();
        this.syncPolling();
      } catch (error) {
        this.setRefusal(error);
      }
    },

    /**
     * The picker holds whatever governed the previous message rather than resetting,
     * so a `grill-me` interview does not silently drop back to the default halfway
     * through. Only resolvable for skills chosen in this session, for the same reason
     * the change markers cannot always be named.
     */
    carryForwardSkill() {
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        const message = this.messages[i];
        if (message.role !== "user" || !message.skillVersionId) {
          continue;
        }
        const skillId = this.skillIdByVersion(message.skillVersionId);
        if (skillId) {
          this.activeSkillId = skillId;
        }
        return;
      }
    },

    skillIdByVersion(versionId) {
      const name = this.skillNames[versionId];
      if (!name) {
        return null;
      }
      const skill = this.skills.find((candidate) => candidate.name === name);
      return skill ? skill.id : null;
    },

    async send() {
      const text = this.composerText.trim();
      if (!text || this.sending) {
        return;
      }

      this.sending = true;
      this.refusal = null;

      // The message belongs in the transcript the moment it is sent, not when the
      // answer comes back. Generation can take up to the 300s cap, and leaving the
      // text sitting in a disabled input reads as "nothing happened". These two are
      // replaced by the server's own rows as soon as the response lands.
      const stamp = new Date().toISOString();
      this.localSeq += 1;
      this.messages.push(
        {
          id: `local-user-${this.localSeq}`,
          seq: this.messages.length,
          role: "user",
          status: "complete",
          text,
          createdAt: stamp,
          isLocal: true,
        },
        {
          id: `local-assistant-${this.localSeq}`,
          seq: this.messages.length + 1,
          role: "assistant",
          status: "pending",
          createdAt: stamp,
          isLocal: true,
        },
      );
      this.composerText = "";

      try {
        if (supportsStreaming()) {
          await this.sendStreaming(text);
        } else {
          await this.sendWhole(text);
        }

        this.loadConversations();
        this.syncPolling();
      } catch (error) {
        // The message stays in the transcript, marked as refused, instead of vanishing
        // and being reported somewhere else on the screen. A transcript that looks
        // untouched after a refusal reads as "sent and ignored" — which is the one
        // thing that did not happen — and it loses the text the user wrote.
        //
        // The composer is deliberately not refilled: the words are on screen, in the
        // place they were meant to go, and the message's own "try again" puts them back
        // for editing. Two copies of the same paragraph is worse than none.
        const described = describeApiError(
          error,
          "Something went wrong sending that message. Try again.",
        );
        this.failLocalMessage(text, {
          ...described,
          // The sensitive-data code. Not a failure so much as the boundary doing its
          // job, and the only case where editing rather than retrying is the way out.
          isBlock: Boolean(described.code && String(described.code).endsWith("-040")),
        });

        if (!this.activeConversationId) {
          // First message: the conversation may exist even though this threw. Refresh
          // the list so it is reachable rather than silently orphaned.
          this.loadConversations();
        }
      } finally {
        this.sending = false;
      }
    },

    /**
     * The non-streaming send, kept rather than removed.
     *
     * The backend deliberately kept `POST …/messages` alive alongside the streaming
     * route, and both call the same service method — one gate order, one write ordering,
     * one set of terminal states — so nothing behaves differently between them. This is
     * the path for a browser that cannot read a response body as it arrives.
     */
    async sendWhole(text) {
      const response = this.activeConversationId
        ? await AiWorkspaceService.sendMessage(this.activeConversationId, text, this.activeSkillId)
        : await AiWorkspaceService.startConversation(text, this.activeSkillId);

      this.applySendResult(response.data || {});
    },

    /**
     * The streaming send.
     *
     * The stream endpoint takes a conversation id, so a first message creates the
     * conversation first. If that create comes back already carrying an answer — the
     * create-carries-first-message shape the service documents — the answer is complete
     * and there is nothing left to stream.
     *
     * Everything that can refuse a message is decided before the stream opens and
     * arrives as an ordinary rejection, so the `catch` in `send` still handles quotas,
     * the kill switch and the sensitive-data filter exactly as it did. Anything that
     * goes wrong *after* the first event arrives comes back through `onError`, because
     * by then there is no HTTP status left to carry it.
     */
    async sendStreaming(text) {
      let conversationId = this.activeConversationId;

      if (!conversationId) {
        const created = await AiWorkspaceService.createConversation(text, this.activeSkillId);

        if (created.answered) {
          this.applySendResult(created.payload);
          return;
        }

        conversationId = created.conversationId;

        if (!conversationId) {
          throw new Error("The conversation was created without an id to send into.");
        }

        // Held here, not left to the stream's `start` event. The conversation exists on
        // the server the moment the create returns, so every later send in this chat has
        // to go into it — including one that follows a refusal or a dropped stream. A
        // send that mints a second conversation is what makes the assistant look like it
        // has forgotten the exchange before it.
        this.activeConversationId = conversationId;
      }

      try {
        await streamMessage({
          conversationId,
          message: text,
          skillId: this.activeSkillId,
          onStart: (payload) => this.onStreamStart(conversationId, payload),
          onDelta: (payload) => this.onStreamDelta(payload),
          onDone: (payload) => this.onStreamDone(payload),
          onError: (payload) => this.onStreamError(payload, text),
        });
      } catch (error) {
        // Everything that can refuse a message is decided before the stream opens, so a
        // throw once `start` has arrived is the socket rather than the answer. Rethrow
        // only while nothing has started; past that it is not a failure to report.
        if (!this.streamingMessageId) {
          throw error;
        }
      }

      // Reached either by a clean end with no `done` — a dropped connection — or by the
      // throw above. Both mean the same thing.
      this.leaveToPolling();
    },

    /**
     * The connection went, but the answer did not.
     *
     * A dropped stream does not stop the generation: the backend keeps consuming the
     * provider stream and still writes the finished answer, so the row resolves on its
     * own and polling finds it. Marking it failed would be wrong twice over — it says
     * the answer is not coming, and it invites a resend that pays for a second one.
     *
     * The partial text is cleared and the row goes back to `pending` rather than being
     * left as `streaming`. None of that text was written anywhere, and a blinking cursor
     * on text that has stopped growing says the answer is still arriving in this window,
     * which it is not. "Working on it" is what is actually true.
     */
    leaveToPolling() {
      const streamingId = this.streamingMessageId;
      this.streamingMessageId = null;

      if (!streamingId) {
        return;
      }

      this.messages = this.messages.map((row) =>
        row.id === streamingId ? { ...row, status: "pending", text: "" } : row,
      );
    },

    /**
     * Every gate has passed and the provider is about to be called.
     *
     * This is where the optimistic pair is replaced by the server's own rows, which is
     * the same swap the non-streaming path makes — the two messages are the same
     * `MessageView` shape either way, so the transcript paints a streamed answer and a
     * reloaded one with one component.
     */
    onStreamStart(conversationId, payload) {
      this.dropLocalMessages();
      this.activeConversationId = payload.conversationId || conversationId;

      if (payload.userMessage) {
        this.messages.push(payload.userMessage);
        this.recordSkillName(payload.userMessage.skillVersionId);
      }

      if (payload.assistantMessage) {
        // `text` is set explicitly, even to empty, before the row is pushed: Vue 2 only
        // makes properties reactive that exist when it observes the object, and the
        // fragments below append to exactly this field.
        const assistant = {
          ...payload.assistantMessage,
          status: payload.assistantMessage.status || "streaming",
          text: payload.assistantMessage.text || "",
        };

        this.messages.push(assistant);
        this.streamingMessageId = assistant.id;
        this.recordSkillName(assistant.skillVersionId);
      }
    },

    /**
     * A fragment arrived.
     *
     * Appended, never assigned: each `delta` is a new piece of the answer, not a running
     * total of it. Treating it as cumulative would look almost right on a short answer
     * and produce a stutter of repeated text on a long one.
     */
    onStreamDelta(payload) {
      if (!payload || !payload.text || !this.streamingMessageId) {
        return;
      }

      const message = this.messages.find((row) => row.id === this.streamingMessageId);
      if (message) {
        message.text += payload.text;
      }
    },

    /**
     * The answer is written to storage.
     *
     * The server's row replaces what was assembled from fragments rather than being
     * merged into it — it is the authoritative text, and it carries the terminal status.
     * `interrupted` here is not a failure: the provider stopped early, almost always at
     * the output-token cap, and the text is real and saved. The transcript says so
     * underneath it.
     */
    onStreamDone(payload) {
      const finished = payload && payload.assistantMessage;
      if (!finished) {
        this.streamingMessageId = null;
        return;
      }

      this.messages = this.messages.map((row) =>
        row.id === this.streamingMessageId || row.id === finished.id ? finished : row,
      );
      this.streamingMessageId = null;
      this.recordSkillName(finished.skillVersionId);
    },

    /**
     * It did not finish, and the stream is about to end.
     *
     * The partial text is dropped: nothing was written to storage, so keeping it on
     * screen would show an answer that will not be there on the next reload. The user
     * message stays where it is with the reason attached, which is the same treatment a
     * refusal gets.
     *
     * `E1018-054` is the one worth being careful about. It means a deploy landed while
     * the model was answering perfectly well — "something went wrong" points the reader
     * at the wrong conclusion, so the wording says it was interrupted and that resending
     * works. `refusal.js` holds that sentence, in step with the backend's own codes.
     */
    onStreamError(payload, text) {
      const streamingId = this.streamingMessageId;
      this.streamingMessageId = null;

      const described = {
        message: (payload && payload.message) || "That answer could not be completed.",
        code: (payload && payload.code) || null,
      };

      // Never reached `start`, so no row was created server-side and the optimistic pair
      // is still what is on screen. It carries the refusal, exactly as it does when the
      // send is rejected outright.
      if (!streamingId) {
        this.failLocalMessage(text, { ...described, isBlock: false });
        return;
      }

      // Past `start`. The user's message was stored — only the answer failed — so the
      // failure belongs on the assistant row, where the transcript already words a
      // provider failure differently from a message that never sent.
      //
      // The partial text goes with it. Nothing was written to storage, so leaving it up
      // would show an answer that will not be there after a reload.
      this.messages = this.messages.map((row) =>
        row.id === streamingId
          ? { ...row, status: "failed", text: "", errorCode: described.code }
          : row,
      );
    },

    /** The finished shape both non-streaming paths come back in. */
    applySendResult(payload) {
      this.dropLocalMessages();

      if (payload.conversationId) {
        this.activeConversationId = payload.conversationId;
      }

      if (payload.userMessage) {
        this.messages.push(payload.userMessage);
        this.recordSkillName(payload.userMessage.skillVersionId);
      }
      if (payload.assistantMessage) {
        this.messages.push(payload.assistantMessage);
        this.recordSkillName(payload.assistantMessage.skillVersionId);
      }
    },

    /**
     * Clear the optimistic pair once the server's own rows supersede them.
     *
     * Refused messages are kept: nothing on the server supersedes them — that is the
     * point — and a later successful send should not quietly erase the record of the
     * one before it that did not go through.
     */
    dropLocalMessages() {
      this.messages = this.messages.filter(
        (message) => !message.isLocal || message.status === "failed",
      );
    },

    /**
     * Turn the optimistic pair into a single refused message carrying the reason.
     *
     * The pending assistant bubble goes: no answer is coming, and leaving it working
     * would be a second thing on screen that is not true.
     */
    failLocalMessage(text, failure) {
      this.messages = this.messages
        .filter((message) => !(message.isLocal && message.role === "assistant"))
        .map((message) =>
          message.isLocal && message.role === "user" && message.text === text
            ? { ...message, status: "failed", failure }
            : message,
        );
    },

    /**
     * Put a refused message back in the composer to be edited or resent.
     *
     * It leaves the transcript as it goes, so the text exists in exactly one place. For
     * a sensitive-data block this is the only copy anywhere — the server stored the
     * category and nothing else.
     */
    retryMessage(message) {
      this.composerText = message.text || "";

      // Only the optimistic copy is ours to take away. A refusal the server recorded
      // stays in the transcript — it is what happened, and it will be there again on
      // the next reload whether or not this screen keeps it.
      if (message.isLocal) {
        this.messages = this.messages.filter((row) => row.id !== message.id);
      }
    },

    recordSkillName(versionId) {
      if (!versionId || this.skillNames[versionId]) {
        return;
      }
      const skill = this.skills.find((candidate) => candidate.id === this.activeSkillId);
      if (skill) {
        this.$set(this.skillNames, versionId, skill.name);
      }
    },

    /**
     * A message left unresolved by a pod restart has a row but no body in Cosmos —
     * the answer is written once, on completion, so mid-generation there is no partial
     * text anywhere to show. Polling re-reads history until the status flips, which
     * goes through the database and so works whichever pod serves it.
     */
    syncPolling() {
      if (this.hasUnresolvedMessage) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },

    startPolling() {
      if (this.pollTimer) {
        return;
      }
      this.pollTimer = setInterval(async () => {
        if (!this.activeConversationId || this.sending) {
          return;
        }
        if (!this.hasUnresolvedMessage) {
          this.stopPolling();
          return;
        }
        try {
          const response = await AiWorkspaceService.getHistory(this.activeConversationId);
          this.messages = response.data || [];
        } catch {
          // A missed tick is harmless: the next one picks the answer up.
        }
      }, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    /**
     * Both of these update the single affected row rather than refetching the list.
     * The server has already told us the operation succeeded, and reloading makes the
     * whole rail flicker and re-sort for a change to one item.
     */
    async renameConversation({ conversation, title }) {
      try {
        await AiWorkspaceService.renameConversation(conversation.id, title);
        this.conversations = this.conversations.map((row) =>
          row.id === conversation.id ? { ...row, title, label: title } : row,
        );
      } catch (error) {
        this.setRefusal(error);
      }
    },

    async hideConversation(conversation) {
      try {
        await AiWorkspaceService.hideConversation(conversation.id);
        this.conversations = this.conversations.filter((row) => row.id !== conversation.id);

        if (this.activeConversationId === conversation.id) {
          this.startNew();
          // Keep the rail up: you deleted what you were reading, so the next thing
          // you want is to pick something else.
          this.historyOpen = true;
        }
      } catch (error) {
        this.setRefusal(error);
      }
    },

    /**
     * Render the backend's own sentence. The wording is deliberate — "your role has no
     * AI quota configured" is a different problem from "you do not have access", and
     * flattening them into a generic message is the whole support burden of the first
     * fortnight.
     */
    setRefusal(error) {
      const described = describeApiError(
        error,
        "Something went wrong sending that message. Try again.",
      );

      this.refusal = {
        ...described,
        // The sensitive-data code. Worth its own treatment: it is not a failure, it
        // is the boundary doing its job.
        isBlock: Boolean(described.code && String(described.code).endsWith("-040")),
      };
    },
  },
};
</script>

<style>
/* Sized to the widest thing inside it — the settings form, four fields across — now
   that the tables are down to three columns apiece. Wide enough that nothing scrolls
   sideways, narrow enough that nothing is stretched to fill space it does not need.
   Unscoped because the dialog belongs to BModal, not to this template. */
.ai-settings-dialog {
  max-width: 840px;
  width: 88vw;
}
</style>

<style scoped>
/* Shimmering silver page behind the conversation; the chat and composer sit on
   top as static white so nothing moves under the text.

   The sweep is a single skewed highlight band translated across a static base,
   NOT an oversized tiled gradient. A tiled diagonal gradient does not meet itself
   at the tile boundary, and that mismatch shows up as a hard vertical line
   travelling through the shimmer. */
.ai-workspace {
  position: relative;
  overflow: hidden;
  height: calc(100vh - 140px);
  min-height: 420px;
  background: linear-gradient(115deg, #e9edf2 0%, #f8fafc 38%, #e6ebf1 62%, #f4f6f9 100%);
}

.ai-workspace::before {
  content: "";
  position: absolute;
  top: -25%;
  bottom: -25%;
  left: 0;
  width: 38%;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-150%) skewX(-18deg);
  animation: ai-shimmer 12s linear infinite;
}

@keyframes ai-shimmer {
  0%   { transform: translateX(-150%) skewX(-18deg); }
  100% { transform: translateX(400%) skewX(-18deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ai-workspace::before { animation: none; opacity: 0; }
}

/* The conversation column, bounded to reading width with razor-thin padding — the
   grey shows as an edge, not as a margin. Positioned so it sits above the sweep,
   which is a positioned pseudo-element. */
.ai-workspace__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 784px;
  margin: 0 auto;
}

/* The rail slides over the transcript rather than displacing it, so opening it
   never reflows an answer mid-read. */
.ai-rail-enter-active,
.ai-rail-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.ai-rail-enter,
.ai-rail-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ai-rail-enter-active,
  .ai-rail-leave-active {
    transition: none;
  }
}

.ai-workspace__main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
}

/* Sits on the right edge, vertically centred, and slides to the panel's edge when
   it opens so it is never underneath it. Above the rail in z-order for the same
   reason. */
.ai-workspace__rail-toggle {
  position: absolute;
  top: 50%;
  right: 2px;
  z-index: 6;
  transform: translateY(-50%);
  padding: 10px 4px;
  border: 0;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: #b3bac6;
  cursor: pointer;
  transition: right 0.18s ease, color 0.15s ease;
}

.ai-workspace__rail-toggle:hover {
  color: #5b6b8c;
}

.ai-workspace__rail-toggle.is-open {
  right: 286px;
}

@media (prefers-reduced-motion: reduce) {
  .ai-workspace__rail-toggle {
    transition: none;
  }
}

/* The chat surface: header plus scrolling body, wrapped as one. */
.ai-workspace__surface {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 2px solid #6eb644;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.ai-chat-header {
  flex: 0 0 auto;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(110, 182, 68, 0.28);
}

.ai-chat-header__title {
  font-size: 13px;
  font-weight: 600;
  color: #2f3542;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.ai-workspace__landing {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.ai-workspace__landing-inner {
  max-width: 520px;
  text-align: center;
}

.ai-workspace__landing-title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
  color: #2f3542;
}

.ai-workspace__landing-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #8a91a0;
}

.ai-workspace__resume {
  margin-top: 18px;
  padding: 7px 14px;
  border: 1px solid #e3e6ec;
  border-radius: 999px;
  background: none;
  font-size: 13px;
  color: #5b6b8c;
  cursor: pointer;
}

.ai-workspace__resume:hover {
  border-color: #b7c0d0;
  background: #f6f7f9;
}

/* Refusals --------------------------------------------------------------- */

.ai-workspace__refusal-wrap {
  padding: 0 12px;
}

.ai-workspace__refusal {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 760px;
  margin: 0 auto 4px;
  padding: 11px 13px;
  border: 1px solid #f0d6b8;
  border-radius: 9px;
  background: #fdf6ec;
  font-size: 13px;
  line-height: 1.5;
  color: #8a5a1a;
}

/* A standing state rather than a refusal: cooler than the amber, and with no dismiss —
   there is nothing to dismiss, the composer is inert until the state changes. */
.ai-workspace__notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 760px;
  margin: 0 auto 4px;
  padding: 11px 13px;
  border: 1px solid #cfd8e3;
  border-radius: 9px;
  background: #f4f7fa;
  font-size: 13px;
  line-height: 1.5;
  color: #40546b;
}

.ai-workspace__refusal.is-block {
  border-color: #f3c9c9;
  background: #fdf0f0;
  color: #a3372c;
}

.ai-workspace__refusal-body {
  flex: 1 1 auto;
}

.ai-workspace__refusal-code {
  margin-top: 3px;
  font-size: 11px;
  opacity: 0.7;
}

.ai-workspace__refusal button {
  border: 0;
  background: none;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
}

.ai-workspace__refusal button:hover {
  opacity: 1;
}

/* Dark theme ------------------------------------------------------------- */

body.full-dark .ai-workspace {
  background: linear-gradient(115deg, #1c2029 0%, #262d3d 38%, #1e2431 62%, #232938 100%);
}

body.full-dark .ai-workspace::before {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0) 100%
  );
}

body.full-dark .ai-workspace__surface {
  background: #232938;
}

body.full-dark .ai-chat-header__title {
  color: #e4e7ee;
}

body.full-dark .ai-workspace__landing-title {
  color: #e4e7ee;
}

body.full-dark .ai-workspace__rail-toggle {
  color: #5a6478;
}

body.full-dark .ai-workspace__rail-toggle:hover {
  color: #9aa6c0;
}

body.full-dark .ai-workspace__resume {
  border-color: #333c50;
  color: #9aa6c0;
}
</style>
